/**
 * Match results: what the ML pipeline produces and what the UI renders.
 *
 * The shape here encodes the product's central claim — that a match is only
 * useful if it comes with its reasoning. A `Candidate` therefore cannot exist
 * without a per-criterion breakdown, and every `MET`/`NOT_MET` verdict must carry
 * an evidence span that was found in the patient's record by string match. That
 * invariant is enforced in code (`isVerdictSupported`) rather than trusted to the
 * component that renders it.
 */
import { z } from 'zod'
import { MatchBandSchema, VerdictSchema, DataSourceTypeSchema, CandidateStateSchema, bandFor } from './vocab.js'
import { IsoInstantSchema, ProbabilitySchema, idSchema } from './zod-util.js'
import type { CandidateId, CriterionId, DocumentId, ModelVersion, PatientId, PseudonymId, SiteId, TrialId, UserId } from './ids.js'

/**
 * A quoted span from the patient's record supporting a verdict.
 *
 * `quote` must appear verbatim in the cited source. This is the anti-hallucination
 * mechanism: the adjudicator is not trusted to be right, it is required to point
 * at something, and the pointer is checked. A verdict whose quote cannot be found
 * is downgraded to `INSUFFICIENT_INFORMATION` before it is ever stored.
 */
export const EvidenceSchema = z.object({
  /** Verbatim text from the source. Checked against the source before storage. */
  quote: z.string().min(1),
  sourceType: DataSourceTypeSchema,
  /** The document the quote came from, for `CLINICAL_NOTE` evidence. */
  documentId: idSchema<DocumentId>('document id').optional(),
  /** A human label for the source, e.g. "Progress note, 2026-03-14" or "Lab: ANC". */
  sourceLabel: z.string().default(''),
  /** Clinical date of the underlying fact — not the date we read it. */
  factDate: z.string().optional(),
  /** Character offsets for UI highlighting. */
  span: z.object({ start: z.number().int().nonnegative(), end: z.number().int().nonnegative() }).optional(),
})
export type Evidence = z.infer<typeof EvidenceSchema>

/**
 * The verdict on one criterion for one patient.
 */
export const CriterionEvaluationSchema = z.object({
  criterionId: idSchema<CriterionId>('criterion id'),
  /** Denormalized so the UI can render the table without fetching the trial. */
  criterionText: z.string().min(1),
  criterionType: z.enum(['INCLUSION', 'EXCLUSION']),
  verdict: VerdictSchema,
  /** Confidence in *this verdict*, distinct from the parser's confidence in the criterion. */
  confidence: ProbabilitySchema,
  evidence: z.array(EvidenceSchema).default([]),
  /**
   * For `INSUFFICIENT_INFORMATION`: what would resolve it. This turns a
   * non-answer into a work item — "order a CMP", "confirm ECOG at visit" — which
   * is the difference between a model that shrugs and one that helps.
   */
  missingData: z.string().optional(),
  /** Which stage decided this: a deterministic rule, the LLM adjudicator, or a human. */
  decidedBy: z.enum(['RULE', 'LLM', 'HUMAN']),
  /** Set when a human overrode the machine verdict. The most valuable label we collect. */
  humanOverride: z
    .object({
      verdict: VerdictSchema,
      byUserId: idSchema<UserId>('user id'),
      note: z.string().default(''),
      at: IsoInstantSchema,
    })
    .optional(),
})
export type CriterionEvaluation = z.infer<typeof CriterionEvaluationSchema>

/**
 * A single reranker feature's contribution, from SHAP.
 *
 * Shown in the clinician view under "why this scored the way it did". Feature
 * attributions explain the *score*; the criterion table explains the *eligibility*.
 * Both are needed, and conflating them misleads: a high score with an unmet
 * exclusion is a bug worth seeing.
 */
export const FeatureContributionSchema = z.object({
  feature: z.string().min(1),
  /** Human-readable label, e.g. "Age within trial bounds". */
  label: z.string().default(''),
  /**
   * The feature's value for this patient, or `null` when the feature was missing.
   *
   * `null` is a real answer here — "we had no value for this" is exactly what a
   * clinician needs to see next to a contribution — so this is the one field in the
   * contract where null and absent mean the same thing rather than null being invalid.
   * Defaulted so both wire forms parse: the matcher omits null-valued keys wholesale
   * (see `_AbsentNotNullRoute` in services/matcher/app/main.py, which exists because Zod
   * `.optional()` rejects an explicit null), and a missing feature arrives as NaN, which
   * has no JSON form and encodes as null.
   */
  value: z.union([z.number(), z.string(), z.boolean(), z.null()]).default(null),
  /** Signed SHAP value in log-odds space. Positive pushes toward eligible. */
  contribution: z.number(),
})
export type FeatureContribution = z.infer<typeof FeatureContributionSchema>

/** Per-stage scores, retained for evaluation and debugging of the pipeline. */
export const PipelineScoresSchema = z.object({
  /** Stage 1 lexical (BM25-style) score. */
  lexical: z.number().optional(),
  /** Stage 1 dense/embedding cosine similarity. */
  dense: z.number().optional(),
  /** Stage 1 fused rank score after reciprocal rank fusion. */
  fused: z.number().optional(),
  /** Stage 2 raw model output before calibration. */
  rerankerRaw: z.number().optional(),
  /** Stage 2 calibrated probability — the number the UI shows. */
  calibrated: ProbabilitySchema,
})
export type PipelineScores = z.infer<typeof PipelineScoresSchema>

/**
 * One scored patient×trial pairing, with its explanation and its lifecycle state.
 *
 * This is the system's central object: the sponsor sees a list of these (without
 * identifiers), the coordinator works a queue of them, the clinician reviews one
 * at a time, and every state transition on one produces a training label.
 */
export const CandidateSchema = z.object({
  id: idSchema<CandidateId>('candidate id'),
  patientId: idSchema<PatientId>('patient id'),
  /** What a sponsor sees in place of `patientId`. */
  pseudonym: idSchema<PseudonymId>('pseudonym'),
  trialId: idSchema<TrialId>('trial id'),
  /** The specific site this candidate would be referred to. */
  siteId: idSchema<SiteId>('site id').optional(),

  /** Calibrated probability that the patient is eligible. Shown to clinicians, not patients. */
  probability: ProbabilitySchema,
  band: MatchBandSchema,
  scores: PipelineScoresSchema,

  /** Per-criterion breakdown. Never empty for a stored candidate. */
  evaluations: z.array(CriterionEvaluationSchema),
  /** Top feature attributions from the reranker, most influential first. */
  topFeatures: z.array(FeatureContributionSchema).default([]),

  /** Rollup counts, denormalized so list views need no client-side aggregation. */
  summary: z.object({
    met: z.number().int().nonnegative(),
    notMet: z.number().int().nonnegative(),
    unknown: z.number().int().nonnegative(),
    /** Unmet criteria that are hard blockers — any one of these means not eligible. */
    blockingUnmet: z.number().int().nonnegative(),
  }),

  /** Distance to the referral site in km, when both sides are geocoded. */
  distanceKm: z.number().nonnegative().optional(),

  state: CandidateStateSchema,
  /** Model that produced this, so a re-score can be attributed and compared. */
  modelVersion: idSchema<ModelVersion>('model version'),
  scoredAt: IsoInstantSchema,
  updatedAt: IsoInstantSchema,
})
export type Candidate = z.infer<typeof CandidateSchema>

/**
 * The sponsor-safe projection of a candidate.
 *
 * Built by a Cloud Function, not by the client. Carries no `patientId`, no
 * evaluations (which quote the chart verbatim and are therefore identifying), and
 * no free text at all — only the pseudonym, the band, and counts.
 */
export const CandidateForSponsorSchema = z.object({
  id: idSchema<CandidateId>('candidate id'),
  pseudonym: idSchema<PseudonymId>('pseudonym'),
  trialId: idSchema<TrialId>('trial id'),
  siteId: idSchema<SiteId>('site id').optional(),
  band: MatchBandSchema,
  state: CandidateStateSchema,
  /** Coarse age band rather than an age — an exact age plus a rare condition re-identifies. */
  ageBand: z.enum(['18-29', '30-39', '40-49', '50-59', '60-69', '70-79', '80+', 'UNDER_18']),
  sex: z.enum(['FEMALE', 'MALE', 'UNKNOWN']),
  summary: CandidateSchema.shape.summary,
  distanceKm: z.number().nonnegative().optional(),
  updatedAt: IsoInstantSchema,
})
export type CandidateForSponsor = z.infer<typeof CandidateForSponsorSchema>

// ---------------------------------------------------------------------------
// Invariants and derivations
// ---------------------------------------------------------------------------

/**
 * Whether a verdict is adequately supported.
 *
 * A definite verdict (`MET`/`NOT_MET`) from a machine requires at least one piece
 * of evidence. Humans may assert a verdict without a quote — they were in the room
 * — and `INSUFFICIENT_INFORMATION` needs no evidence by definition, but should say
 * what is missing.
 */
export function isVerdictSupported(evaluation: CriterionEvaluation): boolean {
  if (evaluation.decidedBy === 'HUMAN') return true
  if (evaluation.verdict === 'MET' || evaluation.verdict === 'NOT_MET') return evaluation.evidence.length > 0
  return true
}

/**
 * Force an unsupported machine verdict down to `INSUFFICIENT_INFORMATION`.
 *
 * Applied on the write path, so an unsupported verdict cannot reach the database
 * even if a model insists on one. This is the guardrail that makes the evidence
 * requirement real rather than aspirational.
 */
export function enforceEvidenceRule(evaluation: CriterionEvaluation): CriterionEvaluation {
  if (isVerdictSupported(evaluation)) return evaluation
  return {
    ...evaluation,
    verdict: 'INSUFFICIENT_INFORMATION',
    confidence: Math.min(evaluation.confidence, 0.5),
    missingData:
      evaluation.missingData ??
      'The system could not point to specific evidence in the record for this criterion.',
  }
}

/** The verdict in force, preferring a human's over the machine's. */
export function effectiveVerdict(evaluation: CriterionEvaluation): CriterionEvaluation['verdict'] {
  return evaluation.humanOverride?.verdict ?? evaluation.verdict
}

/**
 * Recompute the rollup counts from the evaluations.
 *
 * A criterion counts as blocking when it is *decisively* against the patient: an
 * unmet inclusion or a met exclusion. `INSUFFICIENT_INFORMATION` never blocks —
 * that is the recall-first asymmetry again, expressed at the criterion level.
 */
export function summarize(
  evaluations: readonly CriterionEvaluation[],
  isBlocking: (criterionId: string) => boolean
): Candidate['summary'] {
  let met = 0
  let notMet = 0
  let unknown = 0
  let blockingUnmet = 0

  for (const e of evaluations) {
    const verdict = effectiveVerdict(e)
    const against =
      (e.criterionType === 'INCLUSION' && verdict === 'NOT_MET') ||
      (e.criterionType === 'EXCLUSION' && verdict === 'MET')

    if (verdict === 'INSUFFICIENT_INFORMATION') unknown += 1
    else if (against) notMet += 1
    else if (verdict === 'MET' || verdict === 'NOT_MET') met += 1

    if (against && isBlocking(String(e.criterionId))) blockingUnmet += 1
  }

  return { met, notMet, unknown, blockingUnmet }
}

/** Recompute `band` from `probability` and the blocking count. Keeps the two consistent. */
export function deriveBand(candidate: Pick<Candidate, 'probability' | 'summary'>): Candidate['band'] {
  return bandFor(candidate.probability, candidate.summary.blockingUnmet > 0)
}

/**
 * Order the criteria table for a reviewing human.
 *
 * Blockers first (they end the review fastest), then unknowns (which are the
 * actionable work — the labs to order, the questions to ask), then satisfied
 * criteria last, since those need no attention. Optimizing this order is
 * optimizing the coordinator's minutes, which is the metric that decides whether
 * the product gets used.
 */
export function orderForReview(evaluations: readonly CriterionEvaluation[]): CriterionEvaluation[] {
  const rank = (e: CriterionEvaluation): number => {
    const verdict = effectiveVerdict(e)
    const against =
      (e.criterionType === 'INCLUSION' && verdict === 'NOT_MET') ||
      (e.criterionType === 'EXCLUSION' && verdict === 'MET')
    if (against) return 0
    if (verdict === 'INSUFFICIENT_INFORMATION') return 1
    return 2
  }
  return [...evaluations].sort((a, b) => rank(a) - rank(b) || a.confidence - b.confidence)
}

/** Coarse age band for sponsor-facing projections. */
export function ageBandFor(ageYears: number): CandidateForSponsor['ageBand'] {
  if (ageYears < 18) return 'UNDER_18'
  if (ageYears < 30) return '18-29'
  if (ageYears < 40) return '30-39'
  if (ageYears < 50) return '40-49'
  if (ageYears < 60) return '50-59'
  if (ageYears < 70) return '60-69'
  if (ageYears < 80) return '70-79'
  return '80+'
}
